import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { geoLocationApi } from '../../api/geoLocationApi';
import { LocationLogDto, LocationReportDto } from '../../types';

interface GeoLocationState {
  history: LocationLogDto[];
  report: LocationReportDto[];
  loading: boolean;
  error: string | null;
  saveStatus: 'idle' | 'loading' | 'success' | 'error';
}

const initialState: GeoLocationState = {
  history: [],
  report: [],
  loading: false,
  error: null,
  saveStatus: 'idle',
};

export const fetchLocationHistory = createAsyncThunk(
  'geoLocation/fetchHistory',
  async (
    { employeeId, startDate, endDate }: { employeeId: string; startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      return await geoLocationApi.getLocationHistory(employeeId, startDate, endDate);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch location history');
    }
  }
);

export const fetchLocationReport = createAsyncThunk(
  'geoLocation/fetchReport',
  async (
    { employeeIds, startDate, endDate }: { employeeIds: string[]; startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      return await geoLocationApi.getLocationReport(employeeIds, startDate, endDate);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch location report');
    }
  }
);

export const saveLocation = createAsyncThunk(
  'geoLocation/save',
  async (
    data: { employeeId: string; latitude: number; longitude: number; locationName?: string; source?: string },
    { rejectWithValue }
  ) => {
    try {
      return await geoLocationApi.saveLocation(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save location');
    }
  }
);

const geoLocationSlice = createSlice({
  name: 'geoLocation',
  initialState,
  reducers: {
    resetSaveStatus: (state) => {
      state.saveStatus = 'idle';
    },
    resetHistory: (state) => {
      state.history = [];
      state.error = null;
      state.loading = false;
    },
    resetReport: (state) => {
      state.report = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch History
      .addCase(fetchLocationHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload;
      })
      .addCase(fetchLocationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Report
      .addCase(fetchLocationReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocationReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload;
      })
      .addCase(fetchLocationReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Save Location
      .addCase(saveLocation.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(saveLocation.fulfilled, (state) => {
        state.saveStatus = 'success';
      })
      .addCase(saveLocation.rejected, (state) => {
        state.saveStatus = 'error';
      });
  },
});

export const { resetSaveStatus, resetHistory, resetReport } = geoLocationSlice.actions;
export default geoLocationSlice.reducer;
